import axios from 'axios';
import * as cheerio from 'cheerio';
import { format, parseISO, eachDayOfInterval, isAfter, startOfDay } from 'date-fns';
import { getCachedDate, setCachedDate } from './cache';

// Known non-show entries or navigational noise to filter out
const NON_SHOW_ENTRIES = [
  'SearchSearchSearchSearchRecent Posts粵語電台檔案',
  'Search',
  'Recent Posts',
  '粵語電台檔案',
  '香港粵語電台檔案室',
  'HK Cantonese Radio Archives',
  'Archives',
  'Home'
];

// Common Hong Kong radio stations & channels
export const KNOWN_STATIONS = [
  '叱咤903',
  '雷霆881',
  '商業電台',
  '香港電台',
  '新城知訊台',
  '新城財經台',
  '新城采訊台',
  '新城電台',
  '堅離地球',
  '風雲谷',
  'D100',
  'MyRadio',
  '謎米香港',
  '綠豆'
];

/**
 * Validates whether a parsed show name is legitimate
 */
export function isValidShow(name) {
  if (!name || typeof name !== 'string') return false;
  const trimmed = name.trim();
  if (trimmed.length < 2) return false;
  return !NON_SHOW_ENTRIES.some(entry => trimmed === entry || (trimmed.startsWith(entry) && trimmed.length < entry.length + 5));
}

/**
 * Generate formatted date strings (YYYY/MM/DD) between start and end dates
 */
export function parseDateStrings(startDate, endDate) {
  if (!startDate || !endDate) return [];
  const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
  let end = typeof endDate === 'string' ? parseISO(endDate) : endDate;

  const today = startOfDay(new Date());
  if (isAfter(startOfDay(end), today)) {
    end = today;
  }

  if (isAfter(startOfDay(start), startOfDay(end))) {
    return [];
  }

  const days = eachDayOfInterval({ start, end });
  return days.map(day => format(day, 'yyyy/MM/dd'));
}

/**
 * Extract station, show title, and full name from the string preceding the date
 */
export function extractStationAndTitle(beforeDate) {
  const clean = beforeDate.replace(/^[–\-—·\s]+|[–\-—·\s]+$/g, '').trim();
  if (!clean) return { station: '其他', showTitle: '', fullName: '' };

  for (const st of KNOWN_STATIONS) {
    if (clean === st) {
      return { station: st, showTitle: st, fullName: clean };
    }
    if (clean.startsWith(st)) {
      const remaining = clean.substring(st.length).trim();
      return {
        station: st,
        showTitle: remaining || st,
        fullName: clean
      };
    }
  }

  // Fallback: check if the first whitespace-separated token looks like a station
  const parts = clean.split(/\s+/);
  if (parts.length > 1) {
    return {
      station: parts[0],
      showTitle: parts.slice(1).join(' '),
      fullName: clean
    };
  }

  return {
    station: '其他',
    showTitle: clean,
    fullName: clean
  };
}

/**
 * Parse an HTML document and extract all radio show episodes
 */
export function parseShowsFromHtml(html, dateStr) {
  if (!html || typeof html !== 'string') return [];
  const formattedDate = dateStr.replace(/\//g, '-');
  const $ = cheerio.load(html);
  const shows = [];

  // Helper to extract links and labels from a cheerio element
  const extractLinksFromElement = ($container) => {
    const linkDetails = [];
    const links = [];
    const htmlContent = $container.html() || '';

    $container.find('a').each((_, aEl) => {
      const $a = $(aEl);
      const href = $a.attr('href');
      if (!href || !href.startsWith('http')) return;
      if (href.includes('chineseradio.atwebpages.com') || href.includes('wordpress.org')) return;

      const linkText = $a.text().trim() || '下載';

      // Look for contextual label like 【下載位置1】： or 【下載位置】： preceding this link
      let posLabel = '';
      const parentHtml = $a.parent().html() || htmlContent;
      const hrefEscaped = href.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const labelRegex = new RegExp(`【(下載位置\\d*)】[：:]?\\s*<a[^>]*href=["']${hrefEscaped}["']`, 'i');
      const m = parentHtml.match(labelRegex);
      if (m && m[1]) {
        posLabel = m[1];
      }

      // Determine mirror brand name (frge, ouo, uii, mega, drive, etc.)
      let mirror = linkText;
      if (href.includes('frge.top')) mirror = 'frge';
      else if (href.includes('ouo.io')) mirror = 'ouo';
      else if (href.includes('uii.io')) mirror = 'uii';
      else if (href.includes('mega.nz')) mirror = 'mega';
      else if (href.includes('drive.google.com')) mirror = 'gdrive';

      const fullLabel = posLabel ? `${mirror} (${posLabel})` : mirror;

      if (!links.includes(href)) {
        links.push(href);
        linkDetails.push({
          url: href,
          label: fullLabel,
          mirror
        });
      }
    });

    return { links, linkDetails };
  };

  // Approach 1: Parse Gutenberg/WordPress paragraph blocks
  const paragraphs = $('p.wp-block-paragraph, .entry-content p, body p');
  const processedParagraphs = new Set();

  paragraphs.each((_, pEl) => {
    const $p = $(pEl);
    const text = $p.text().trim();
    if (!text || text === '–' || text === '-' || text === '—') return;

    // Check if paragraph contains an episode date
    const dateMatch = text.match(/(\d{4}-\d{2}-\d{2})/);
    if (!dateMatch) return;

    // Split paragraph by <br> or newlines to get lines
    const pHtml = $p.html() || '';
    const lines = pHtml
      .split(/<br\s*[\/]?>|\n/i)
      .map(line => $('<div>' + line + '</div>').text().trim())
      .filter(Boolean);

    // Find the title line containing the date
    const titleLine = lines.find(l => l.includes(dateMatch[1])) || lines[0];
    const lineDateMatch = titleLine.match(/(\d{4}-\d{2}-\d{2})/);
    if (!lineDateMatch) return;

    const beforeDate = titleLine.substring(0, lineDateMatch.index).trim();
    const topic = titleLine.substring(lineDateMatch.index + lineDateMatch[0].length).trim();
    const { station, showTitle, fullName } = extractStationAndTitle(beforeDate);

    if (!isValidShow(fullName)) return;

    // Extract retention/expiry
    const expiryMatch = text.match(/【開放時間】[：:]?\s*([^\n\r]+)/);
    const expiry = expiryMatch ? expiryMatch[1].trim() : '';

    // Extract hosts
    const hostsMatch = text.match(/【節目主持】[：:]?\s*([^\n\r]+)/);
    const hosts = hostsMatch ? hostsMatch[1].trim() : '';

    // Extract download links
    const { links, linkDetails } = extractLinksFromElement($p);

    processedParagraphs.add(pEl);
    shows.push({
      name: fullName,
      showTitle,
      station,
      date: formattedDate,
      topic,
      hosts,
      expiry,
      links,
      linkDetails
    });
  });

  // Approach 2 Fallback: If no paragraph matched (older themes/plain posts), parse split by separators
  if (shows.length === 0) {
    const bodyHtml = $('body').html() || '';
    const sections = bodyHtml.split(/&#8211;|–|<hr\s*[\/]?>/i);

    for (const section of sections) {
      const $sec = $('<div>' + section + '</div>');
      const text = $sec.text().trim();
      const dateMatch = text.match(/(\d{4}-\d{2}-\d{2})/);
      if (!dateMatch) continue;

      const lines = text.split(/\n+/).map(l => l.trim()).filter(Boolean);
      const titleLine = lines.find(l => l.includes(dateMatch[1]));
      if (!titleLine) continue;

      const lineDateMatch = titleLine.match(/(\d{4}-\d{2}-\d{2})/);
      if (!lineDateMatch) continue;

      const beforeDate = titleLine.substring(0, lineDateMatch.index).trim();
      const topic = titleLine.substring(lineDateMatch.index + lineDateMatch[0].length).trim();
      const { station, showTitle, fullName } = extractStationAndTitle(beforeDate);

      if (!isValidShow(fullName)) continue;

      const expiryMatch = text.match(/【開放時間】[：:]?\s*([^\n\r]+)/);
      const hostsMatch = text.match(/【節目主持】[：:]?\s*([^\n\r]+)/);
      const { links, linkDetails } = extractLinksFromElement($sec);

      shows.push({
        name: fullName,
        showTitle,
        station,
        date: formattedDate,
        topic,
        hosts: hostsMatch ? hostsMatch[1].trim() : '',
        expiry: expiryMatch ? expiryMatch[1].trim() : '',
        links,
        linkDetails
      });
    }
  }

  return shows;
}

/**
 * Build URL proxy chain
 */
function getProxyEndpoints(targetUrl) {
  const endpoints = [];

  // 1. First-party internal proxy (Cloudflare Pages Function / local dev setupProxy)
  endpoints.push({
    type: 'internal',
    url: `/api/proxy?url=${encodeURIComponent(targetUrl)}`
  });

  // 2. Custom Proxy defined by user (if configured)
  try {
    const custom = localStorage.getItem('radio_custom_proxy');
    if (custom && custom.trim()) {
      const trimmed = custom.trim();
      const proxyUrl = trimmed.includes('%s')
        ? trimmed.replace('%s', encodeURIComponent(targetUrl))
        : trimmed.endsWith('=') || trimmed.endsWith('?')
          ? `${trimmed}${encodeURIComponent(targetUrl)}`
          : `${trimmed}/${encodeURIComponent(targetUrl)}`;
      endpoints.push({ type: 'custom', url: proxyUrl });
    }
  } catch (e) {
    // ignore localstorage errors
  }

  // 3. Reliable Public CORS Proxies with proper URL encoding
  endpoints.push(
    { type: 'allorigins-raw', url: `https://api.allorigins.win/raw?url=${encodeURIComponent(targetUrl)}` },
    { type: 'codetabs', url: `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(targetUrl)}` },
    { type: 'thingproxy', url: `https://thingproxy.freeboard.io/fetch/${targetUrl}` },
    { type: 'allorigins-json', url: `https://api.allorigins.win/get?url=${encodeURIComponent(targetUrl)}`, json: true }
  );

  return endpoints;
}

/**
 * Fetch HTML for a specific date with proxy fallback and timeout
 */
export async function fetchShowsForDate(dateStr, options = {}) {
  const { signal, bypassCache = false } = options;

  // Check local cache first unless explicitly bypassed
  if (!bypassCache) {
    const cached = getCachedDate(dateStr);
    if (cached !== null) {
      return { shows: cached, fromCache: true };
    }
  }

  const targetUrl = `http://chineseradio.atwebpages.com/${dateStr}/`;
  const endpoints = getProxyEndpoints(targetUrl);
  let lastError = null;

  for (const endpoint of endpoints) {
    if (signal && signal.aborted) {
      throw new Error('Scraping cancelled by user');
    }

    try {
      const response = await axios.get(endpoint.url, {
        signal,
        timeout: 9000,
        validateStatus: status => (status >= 200 && status < 300) || status === 404
      });

      if (response.status === 404) {
        // Legitimate empty date
        setCachedDate(dateStr, []);
        return { shows: [], fromCache: false };
      }

      let html = response.data;
      if (endpoint.json && html && typeof html === 'object' && html.contents) {
        html = html.contents;
      }

      if (typeof html === 'string' && (html.includes('香港粵語電台檔案室') || html.includes('wp-block-paragraph') || html.includes('entry-content') || html.includes('404 Not Found') || html.includes('Oops! That page can’t be found'))) {
        if (html.includes('Oops! That page can’t be found') || html.includes('404 Not Found')) {
          setCachedDate(dateStr, []);
          return { shows: [], fromCache: false };
        }

        const parsedShows = parseShowsFromHtml(html, dateStr);
        setCachedDate(dateStr, parsedShows);
        return { shows: parsedShows, fromCache: false };
      }
    } catch (err) {
      if (signal && signal.aborted) {
        throw new Error('Scraping cancelled by user');
      }
      lastError = err;
      // Continue to next proxy in the fallback chain
    }
  }

  console.warn(`All proxies failed for ${dateStr}:`, lastError ? lastError.message : 'Unknown error');
  return { shows: [], fromCache: false, error: lastError };
}

/**
 * Concurrency helper to run async tasks in parallel with a concurrency pool
 */
async function asyncPool(limit, array, iteratorFn) {
  const ret = [];
  const executing = new Set();

  for (const item of array) {
    const p = Promise.resolve().then(() => iteratorFn(item, array));
    ret.push(p);
    executing.add(p);

    const clean = () => executing.delete(p);
    p.then(clean, clean);

    if (executing.size >= limit) {
      await Promise.race(executing);
    }
  }

  return Promise.all(ret);
}

/**
 * Fetch all shows within the specified date range
 */
export async function fetchShows(startDate, endDate, progressCallback = null, options = {}) {
  const { signal, bypassCache = false, concurrency = 3 } = options;

  if (!startDate || !endDate) {
    throw new Error('Missing dates');
  }

  const dateStrings = parseDateStrings(startDate, endDate);
  if (dateStrings.length === 0) {
    return { shows: [], showsByName: {}, rawEpisodes: [] };
  }

  const rawEpisodes = [];
  const showsByName = {};
  let processedDates = 0;
  let cachedCount = 0;
  let errorCount = 0;

  const notifyProgress = (currentDate = null) => {
    if (typeof progressCallback === 'function') {
      try {
        progressCallback({
          shows: Object.keys(showsByName),
          showsByName: { ...showsByName },
          rawEpisodes: [...rawEpisodes],
          currentDate,
          progress: {
            processed: processedDates,
            total: dateStrings.length,
            percentage: Math.round((processedDates / dateStrings.length) * 100),
            cachedCount,
            errorCount
          }
        });
      } catch (e) {
        console.error('Progress callback error:', e);
      }
    }
  };

  // Initial update
  notifyProgress();

  await asyncPool(concurrency, dateStrings, async (dateStr) => {
    if (signal && signal.aborted) {
      throw new Error('Scraping cancelled by user');
    }

    try {
      const result = await fetchShowsForDate(dateStr, { signal, bypassCache });
      if (result.fromCache) cachedCount++;
      if (result.error) errorCount++;

      if (result.shows && result.shows.length > 0) {
        rawEpisodes.push(...result.shows);
        for (const show of result.shows) {
          if (!showsByName[show.name]) {
            showsByName[show.name] = [];
          }
          showsByName[show.name].push(show);
        }
      }
    } catch (err) {
      if (signal && signal.aborted) throw err;
      errorCount++;
      console.error(`Error processing date ${dateStr}:`, err);
    } finally {
      processedDates++;
      notifyProgress(dateStr);
    }
  });

  // Sort rawEpisodes and showsByName chronologically (oldest to newest)
  rawEpisodes.sort((a, b) => a.date.localeCompare(b.date));
  for (const name in showsByName) {
    showsByName[name].sort((a, b) => a.date.localeCompare(b.date));
  }

  // Final notification
  notifyProgress();

  return {
    shows: Object.keys(showsByName),
    showsByName,
    rawEpisodes
  };
}
